/**
 * @fileoverview Shared inline contact create/edit form.
 * @module ui-admin/components/shared-forms
 *
 * Responsibilities:
 * - Render the canonical "create or edit a system contact" form used by
 *   ContactsPanel, ContactSearchTypeahead, the people module, and the
 *   inspection-conductor contacts module.
 * - Drive ContactFormValues state via useContactForm (sync + async validation).
 *
 * Dependencies:
 * - react, lucide-react (peer)
 * - @firesquirrel/core-domain (contactFormSchema, contactLabels, validationHelpers)
 * - useContactForm (sibling) — owns form state + validation
 * - ValidatedEmailInput (../ValidatedEmailInput) — email widget with MX check
 *
 * Architecture:
 * - Presentation-only React component; no Dexie or service access.
 * - The list of available role pills is host-injectable via the
 *   `availableRoles` prop. When omitted, the component falls back to the
 *   ui-admin useContactRoles hook (which reads from the ui-admin lookup
 *   stub). Tenant-web/mobile-pwa hosts MUST pass their own roles list
 *   sourced from Dexie via their own useContactRoles to satisfy the
 *   local-first rule.
 *
 * @component ContactInlineForm
 *
 * Notes:
 * - Promoted from web/tenant-web/src/components/shared-forms/ContactInlineForm.tsx.
 * - The submit/cancel/initialValues contract is unchanged from the legacy
 *   tenant-web implementation; existing callers do not need to change.
 */

import React, { useCallback, useRef } from 'react';
import { X, ChevronDown, Plus, Trash2, Star, Loader2 } from 'lucide-react';
import {
  PHONE_TYPES,
  EMAIL_TYPES,
  createPhoneEntry,
  createEmailEntry,
  CONTACT_FORM_LABELS,
  CONTACT_FIELD_LABELS,
  formatPhoneNumber,
  resolveLookupDisplayValue,
} from '@firesquirrel/core-domain';
import type {
  ContactFormValues,
  PhoneEntry,
  EmailEntry,
  PhoneType,
  EmailType,
  AddressProximity,
} from '@firesquirrel/core-domain';
import type { PostalAddress } from '@firesquirrel/core-domain';
import { useContactRoles } from '../../hooks/useContactRoles';
import { useContactForm } from './useContactForm';
import ValidatedEmailInput from '../ValidatedEmailInput';
import { StructuredAddressField } from './StructuredAddressField';
import type { GridTheme } from '../../contexts/ThemeContext';

export interface ContactInlineFormProps {
  /** Pre-fill name (e.g. from search text) */
  prefillName?: string;
  /** Theme for styling consistency with surrounding UI */
  theme: GridTheme;
  /**
   * Called with validated form values when user clicks Create/Save. May return
   * a Promise; the form awaits it and keeps the submit button disabled until it
   * settles, preventing a double-submit during async persistence.
   */
  onSubmit: (values: ContactFormValues) => void | Promise<void>;
  /** Called when user clicks Cancel or X */
  onCancel: () => void;
  /** If provided, form starts in edit mode pre-filled with these values */
  initialValues?: ContactFormValues;
  /** Button label override (default: "Create & Link") */
  submitLabel?: string;
  /**
   * Optional list of role display names to show as toggle pills. When omitted,
   * the component falls back to the host-neutral useContactRoles hook from
   * ui-admin. Local-first hosts (tenant-web / mobile-pwa) should pass roles
   * sourced from their Dexie-backed lookup service.
   */
  availableRoles?: string[];
  /**
   * Public Mapbox client token (host-injected) powering the as-you-type
   * address candidate picker. When omitted the address block degrades to plain
   * structured inputs + USPS validation; local-first hosts pass their token
   * (e.g. tenant-web getTenantWebConfig().mapboxToken).
   */
  mapboxToken?: string;
  /**
   * Optional bias point (e.g. the tenant's department coverage centroid) so
   * as-you-type address suggestions near the department rank above equally-
   * valid distant matches. Never excludes results. Local-first hosts should
   * source this from their tenant record (e.g. `boundaryCentroid`).
   */
  mapboxProximity?: AddressProximity;
  /**
   * Optional property address passed from parent entity so user can click
   * "Copy Property Address" to pre-fill line1/line2/city/state/zip matching SLI.
   */
  propertyAddress?: PostalAddress;
  /**
   * Called when user clicks "Delete Contact" on the SLI-styled contact form.
   */
  onDelete?: () => void;
  /**
   * Mailing-address validation mode, forwarded to StructuredAddressField.
   * Defaults to `true` (Mapbox assist + USPS standardization) — the existing
   * contact-form behavior. Pass `false` for hosts that only want assisted
   * typing without postal deliverability confirmation.
   */
  requireAddressValidation?: boolean;
  /**
   * Layout orchestration hint owned by the host page (not trapped in the form).
   * - 'stack' (default): every field group stacks vertically. Correct for the
   *   narrow slide-out edit panels and the mobile-pwa ContactsPanel.
   * - 'two-column': on >= md viewports the Phone and Email lists sit side by
   *   side, roughly halving the vertical height. Used by the wide desktop
   *   "New Contact" modal to cut excessive scrolling. Always single-column
   *   below md so it stays usable on small screens.
   */
  layout?: 'stack' | 'two-column';
}

function ToggleSwitch({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
          checked ? 'bg-[#006bb6]' : 'bg-gray-300'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
      <span className="text-xs font-medium text-gray-700 select-none">
        {checked ? 'Yes' : 'No'}
      </span>
    </div>
  );
}

export function ContactInlineForm({
  prefillName,
  theme: t,
  onSubmit,
  onCancel,
  onDelete,
  propertyAddress,
  initialValues,
  submitLabel,
  availableRoles: availableRolesProp,
  mapboxToken,
  mapboxProximity,
  requireAddressValidation = true,
  layout = 'stack',
}: ContactInlineFormProps) {
  const twoCol = layout === 'two-column';
  const { form, errors, emailAsyncErrors, formError, isSubmitting, updateField, validateAndSubmit, setForm } = useContactForm({ prefillName });
  const fallbackRoles = useContactRoles();
  const availableRoles = availableRolesProp ?? fallbackRoles;

  // Load initial values for edit mode — mount-only.
  // DFE-018 fix: the parent (ContactsPanel) recreates the initialValues object
  // on every render because it's built inline from the contact list. When
  // useSyncRefresh triggers a parent re-render, the new object reference caused
  // this effect to re-fire and reset the form — wiping any in-progress user edits
  // (e.g. a partially typed mailing address). The ref guard ensures we only
  // populate the form once when the edit panel opens.
  const initializedRef = useRef(false);
  // Load initial values for edit mode.
  // Defensive normalization: callers can hand us a contact loaded from Dexie
  // (whose `Contact` type has no `defaultRoles` field) or a partially-populated
  // MockContact, which would leave list-shaped fields as `undefined` and crash
  // every `.includes` / `.map` below. Coerce them to empty arrays so the form
  // is always renderable regardless of caller hygiene.
  React.useEffect(() => {
    if (initialValues && !initializedRef.current) {
      initializedRef.current = true;
      // Normalize phone/email entries: legacy Dexie records may store `type` as
      // "primary" (conflating isPrimary with the type enum) or other non-standard
      // strings. Coerce to a valid enum member so Zod validation passes on save.
      const phoneTypesSet = new Set<string>(PHONE_TYPES);
      const emailTypesSet = new Set<string>(EMAIL_TYPES);
      const normalizedPhones = (Array.isArray(initialValues.phoneNumbers) ? initialValues.phoneNumbers : []).map((p: PhoneEntry) => {
        const typeStr = resolveLookupDisplayValue(p.type, true);
        return {
          _id: p._id || `ph-${crypto.randomUUID()}`,
          type: (phoneTypesSet.has(typeStr) ? typeStr : 'Office') as PhoneType,
          number: p.number ?? '',
          extension: p.extension,
          // Legacy Dexie records may store type as "primary" (conflating isPrimary
          // with the type enum).
          isPrimary: p.isPrimary === true || typeStr === 'primary',
        };
      });
      const normalizedEmails = (Array.isArray(initialValues.emails) ? initialValues.emails : []).map((e: EmailEntry) => {
        const typeStr = resolveLookupDisplayValue(e.type, true);
        return {
          _id: e._id || `em-${crypto.randomUUID()}`,
          type: (emailTypesSet.has(typeStr) ? typeStr : 'Business') as EmailType,
          email: e.email ?? '',
          isPrimary: e.isPrimary === true || typeStr === 'primary',
        };
      });
      const nameParts = (initialValues.name || '').trim().split(' ');
      const firstName = initialValues.firstName || nameParts[0] || '';
      const lastName = initialValues.lastName || nameParts.slice(1).join(' ') || '';
      setForm({
        ...initialValues,
        firstName,
        lastName,
        isOccupant: initialValues.isOccupant ?? (initialValues.defaultRoles || []).includes('occupant'),
        isEmergency: initialValues.isEmergency ?? (initialValues.defaultRoles || []).includes('emergency-contact'),
        shouldReceiveReports: initialValues.shouldReceiveReports ?? false,
        notes: initialValues.notes ?? '',
        defaultRoles: (Array.isArray(initialValues.defaultRoles) ? initialValues.defaultRoles : [])
          .map((r: any) => resolveLookupDisplayValue(r))
          .filter(Boolean),
        phoneNumbers: normalizedPhones,
        emails: normalizedEmails,
      });
    }
  }, [initialValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Mailing address ----
  // Keep the legacy freeform `address` string in sync (single-line) so existing
  // consumers that read contact.address keep working while the structured
  // mailingAddress becomes the system-wide source of truth.
  const handleAddressChange = useCallback((next: PostalAddress | undefined) => {
    // StructuredAddressField emits undefined when every field is blank. This
    // form owns the mailingAddress field, so that unambiguously means "user
    // cleared the address" — translate to the null sentinel so the write
    // layer persists the removal instead of treating the key as absent.
    updateField('mailingAddress', next ?? null);
    const singleLine = next
      ? [next.line1, next.line2, [next.city, next.state, next.postalCode].filter(Boolean).join(', ')]
          .filter(Boolean)
          .join(', ')
      : '';
    updateField('address', singleLine);
  }, [updateField]);

  const handleCopyPropertyAddress = useCallback(() => {
    if (!propertyAddress) return;
    handleAddressChange({
      line1: propertyAddress.line1 || '',
      line2: propertyAddress.line2 || undefined,
      city: propertyAddress.city || '',
      state: propertyAddress.state || '',
      postalCode: propertyAddress.postalCode || '',
      countryCode: propertyAddress.countryCode || 'US',
      validationStatus: 'needsValidation',
    });
  }, [propertyAddress, handleAddressChange]);

  const handleSubmit = useCallback(() => {
    validateAndSubmit(onSubmit);
  }, [validateAndSubmit, onSubmit]);

  // ---- Phone list helpers ----
  const addPhone = useCallback(() => {
    updateField('phoneNumbers', [...form.phoneNumbers, createPhoneEntry('Mobile', false)]);
  }, [form.phoneNumbers, updateField]);

  const removePhone = useCallback((id: string) => {
    const updated = form.phoneNumbers.filter((p: PhoneEntry) => p._id !== id);
    if (updated.length > 0 && !updated.some((p: PhoneEntry) => p.isPrimary)) {
      updated[0].isPrimary = true;
    }
    updateField('phoneNumbers', updated);
  }, [form.phoneNumbers, updateField]);

  const updatePhone = useCallback((id: string, patch: Partial<PhoneEntry>) => {
    updateField('phoneNumbers', form.phoneNumbers.map((p: PhoneEntry) =>
      p._id === id ? { ...p, ...patch } : patch.isPrimary ? { ...p, isPrimary: false } : p
    ));
  }, [form.phoneNumbers, updateField]);

  // ---- Email list helpers ----
  const addEmail = useCallback(() => {
    updateField('emails', [...form.emails, createEmailEntry('Personal', false)]);
  }, [form.emails, updateField]);

  const removeEmail = useCallback((id: string) => {
    const updated = form.emails.filter((e: EmailEntry) => e._id !== id);
    if (updated.length > 0 && !updated.some((e: EmailEntry) => e.isPrimary)) {
      updated[0].isPrimary = true;
    }
    updateField('emails', updated);
  }, [form.emails, updateField]);

  const updateEmail = useCallback((id: string, patch: Partial<EmailEntry>) => {
    updateField('emails', form.emails.map((e: EmailEntry) =>
      e._id === id ? { ...e, ...patch } : patch.isPrimary ? { ...e, isPrimary: false } : e
    ));
  }, [form.emails, updateField]);

  // ---- Role toggle ----
  const toggleRole = useCallback((role: string) => {
    const current = form.defaultRoles;
    const next = current.includes(role) ? current.filter((r: string) => r !== role) : [...current, role];
    updateField('defaultRoles', next);
  }, [form.defaultRoles, updateField]);

  // Mobile-aware touch targets: inputs are >= 36px tall, role pills + buttons
  // >= 36px, icon mini-buttons get a 32x32 tap area while staying visually
  // compact. The form renders both in tenant-web side panels (desktop) and
  // inside the mobile-pwa ContactsPanel shell on phones, so these tokens
  // must read well on both surfaces.
  const labelCls = 'text-[11px] font-semibold text-gray-500 uppercase tracking-wider';
  const inputCls = 'w-full mt-1 px-3 py-2 text-sm rounded-md border focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-hidden min-h-[36px]';
  const errorCls = 'text-[11px] text-red-500 mt-0.5';
  const miniBtn = 'p-1.5 rounded-sm transition-colors min-w-[32px] min-h-[32px] inline-flex items-center justify-center';

  return (
    <div className="mt-2 rounded-lg border p-4 space-y-3" style={{ borderColor: t.accent + '50', background: t.cardBg }}>
      {/* Top action buttons: Delete Contact & Copy Property Address (matching SLI Image 1) */}
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <span className="text-xs font-semibold" style={{ color: t.textPrimary }}>
          {initialValues ? CONTACT_FORM_LABELS.editAction : CONTACT_FORM_LABELS.singular}
        </span>
        <div className="flex items-center gap-2">
          {onDelete && initialValues && (
            <button
              type="button"
              onClick={onDelete}
              className="px-3.5 py-1.5 text-xs font-semibold rounded-sm bg-[#0B3B60] text-white hover:bg-[#082a45] transition-colors"
            >
              Delete Contact
            </button>
          )}
          {propertyAddress && (
            <button
              type="button"
              onClick={handleCopyPropertyAddress}
              className="px-3.5 py-1.5 text-xs font-semibold rounded-sm bg-[#0B3B60] text-white hover:bg-[#082a45] transition-colors"
            >
              Copy Property Address
            </button>
          )}
        </div>
        <button onClick={onCancel} className="p-1 rounded-sm text-gray-400 hover:text-red-500 transition-colors">
          <X className="h-4 w-4" />
        </button>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {/* LEFT COLUMN: Title, Business Name *, First Name *, Last Name *, Occupant, Emergency, Should Receive Reports, Email */}
        <div className="space-y-3">
          {/* Title */}
          <div>
            <label className={labelCls}>Title</label>
            <input
              type="text"
              value={form.title ?? ''}
              onChange={e => updateField('title', e.target.value)}
              className={`${inputCls} border-gray-300`}
              placeholder="Title"
            />
          </div>

          {/* Business Name * */}
          <div>
            <label className={labelCls}>Business Name *</label>
            <input
              type="text"
              value={form.company ?? ''}
              onChange={e => updateField('company', e.target.value)}
              className={`${inputCls} ${errors.company ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
              placeholder="Business Name"
            />
            {errors.company && <p className={errorCls}>{errors.company}</p>}
          </div>

          {/* First Name * */}
          <div>
            <label className={labelCls}>First Name *</label>
            <input
              type="text"
              value={form.firstName ?? ''}
              onChange={e => updateField('firstName', e.target.value)}
              className={`${inputCls} ${errors.firstName || errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
              placeholder="First Name"
              autoFocus
            />
            {errors.firstName && <p className={errorCls}>{errors.firstName}</p>}
          </div>

          {/* Last Name * */}
          <div>
            <label className={labelCls}>Last Name *</label>
            <input
              type="text"
              value={form.lastName ?? ''}
              onChange={e => updateField('lastName', e.target.value)}
              className={`${inputCls} ${errors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
              placeholder="Last Name"
            />
            {errors.lastName && <p className={errorCls}>{errors.lastName}</p>}
          </div>

          {/* Occupant */}
          <div className="py-0.5">
            <label className={labelCls}>Occupant</label>
            <div className="mt-1">
              <ToggleSwitch
                label="Occupant"
                checked={Boolean(form.isOccupant)}
                onChange={val => updateField('isOccupant', val)}
              />
            </div>
          </div>

          {/* Emergency */}
          <div className="py-0.5">
            <label className={labelCls}>Emergency</label>
            <div className="mt-1">
              <ToggleSwitch
                label="Emergency"
                checked={Boolean(form.isEmergency)}
                onChange={val => updateField('isEmergency', val)}
              />
            </div>
          </div>

          {/* Should Receive Reports */}
          <div className="py-0.5">
            <label className={labelCls}>Should Receive Reports</label>
            <div className="mt-1">
              <ToggleSwitch
                label="Should Receive Reports"
                checked={Boolean(form.shouldReceiveReports ?? false)}
                onChange={val => updateField('shouldReceiveReports', val)}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <div className="flex items-center justify-between">
              <label className={labelCls}>{CONTACT_FIELD_LABELS.emails.label} *</label>
              <button
                type="button"
                onClick={addEmail}
                className="flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700 transition-colors min-h-[32px] px-1"
              >
                <Plus className="h-2.5 w-2.5" />{CONTACT_FORM_LABELS.addEmail}
              </button>
            </div>
            <div className="space-y-1.5 mt-1">
              {form.emails.map((em: EmailEntry) => (
                <div key={em._id} className="flex items-center gap-1">
                  <button type="button" title={CONTACT_FORM_LABELS.primary}
                    onClick={() => updateEmail(em._id, { isPrimary: true })}
                    className={`${miniBtn} ${em.isPrimary ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}>
                    <Star className="h-3 w-3" fill={em.isPrimary ? 'currentColor' : 'none'} />
                  </button>
                  <div className="relative">
                    <select value={em.type}
                      onChange={e => updateEmail(em._id, { type: e.target.value as EmailType })}
                      className="appearance-none pl-2.5 pr-6 py-1.5 text-xs rounded-sm border border-gray-300 bg-white focus:ring-1 focus:ring-orange-500 outline-hidden min-h-[32px]">
                      {EMAIL_TYPES.map(et => <option key={et} value={et}>{et}</option>)}
                    </select>
                    <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 h-2.5 w-2.5 text-gray-400 pointer-events-none" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <ValidatedEmailInput value={em.email}
                      onChange={(v) => updateEmail(em._id, { email: v })}
                      className={`w-full px-2 py-1 text-xs rounded-sm border focus:ring-1 focus:ring-orange-500 outline-hidden pr-7 ${errors.emails || emailAsyncErrors.has(em._id) ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                      placeholder={CONTACT_FIELD_LABELS.emails.placeholder} />
                    {emailAsyncErrors.has(em._id) && (
                      <p className={errorCls}>{emailAsyncErrors.get(em._id)}</p>
                    )}
                  </div>
                  {form.emails.length > 1 && (
                    <button type="button" onClick={() => removeEmail(em._id)}
                      className={`${miniBtn} text-gray-300 hover:text-red-500`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors.emails && <p className={errorCls}>{errors.emails}</p>}
          </div>
        </div>

        {/* RIGHT COLUMN: Address Line 1, Address Line 2, City, State, Zip, Validate Address, Phones, Notes */}
        <div className="space-y-3">
          <StructuredAddressField
            theme={t}
            value={form.mailingAddress}
            onChange={handleAddressChange}
            mapboxToken={mapboxToken}
            mapboxProximity={mapboxProximity}
            labelCls={labelCls}
            inputCls={inputCls}
            errorCls={errorCls}
            variant="sli"
          />
          {(errors.mailingAddress || errors.address) && (
            <p className={errorCls} role="alert">{errors.mailingAddress ?? errors.address}</p>
          )}

          {/* Phones */}
          <div>
            <div className="flex items-center justify-between">
              <label className={labelCls}>{CONTACT_FIELD_LABELS.phoneNumbers.label} *</label>
              <button type="button" onClick={addPhone}
                className="flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700 transition-colors min-h-[32px] px-1">
                <Plus className="h-2.5 w-2.5" />{CONTACT_FORM_LABELS.addPhone}
              </button>
            </div>
            <div className="space-y-1.5 mt-1">
              {form.phoneNumbers.map((ph: PhoneEntry) => (
                <div key={ph._id} className="flex items-center gap-1">
                  <button type="button" title={CONTACT_FORM_LABELS.primary}
                    onClick={() => updatePhone(ph._id, { isPrimary: true })}
                    className={`${miniBtn} ${ph.isPrimary ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}>
                    <Star className="h-3 w-3" fill={ph.isPrimary ? 'currentColor' : 'none'} />
                  </button>
                  <div className="relative">
                    <select value={ph.type}
                      onChange={e => updatePhone(ph._id, { type: e.target.value as PhoneType })}
                      className="appearance-none pl-2.5 pr-6 py-1.5 text-xs rounded-sm border border-gray-300 bg-white focus:ring-1 focus:ring-orange-500 outline-hidden min-h-[32px]">
                      {PHONE_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                    </select>
                    <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 h-2.5 w-2.5 text-gray-400 pointer-events-none" />
                  </div>
                  <input type="tel" value={ph.number}
                    onChange={e => updatePhone(ph._id, { number: formatPhoneNumber(e.target.value) })}
                    className={`flex-1 px-2 py-1 text-xs rounded-sm border focus:ring-1 focus:ring-orange-500 outline-hidden ${errors.phoneNumbers ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                    placeholder={CONTACT_FIELD_LABELS.phoneNumbers.placeholder} />
                  <input type="text" value={ph.extension ?? ''}
                    onChange={e => updatePhone(ph._id, { extension: e.target.value })}
                    className="w-12 px-1.5 py-1 text-xs rounded-sm border border-gray-300 focus:ring-1 focus:ring-orange-500 outline-hidden"
                    placeholder={CONTACT_FIELD_LABELS.extension.placeholder} />
                  {form.phoneNumbers.length > 1 && (
                    <button type="button" onClick={() => removePhone(ph._id)}
                      className={`${miniBtn} text-gray-300 hover:text-red-500`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors.phoneNumbers && <p className={errorCls}>{errors.phoneNumbers}</p>}
          </div>

          {/* Notes */}
          <div>
            <div className="flex items-center justify-between">
              <label className={labelCls}>Notes</label>
              {form.notes && (
                <button
                  type="button"
                  onClick={() => updateField('notes', '')}
                  className="text-[11px] text-blue-600 hover:underline"
                >
                  Clear Text
                </button>
              )}
            </div>
            <textarea
              rows={4}
              value={form.notes ?? ''}
              onChange={e => updateField('notes', e.target.value)}
              className={`${inputCls} border-gray-300 mt-1 resize-none`}
              placeholder="Notes"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      {formError && (
        <p className="text-xs font-medium text-red-600" role="alert">{formError}</p>
      )}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <p className="text-[10px]" style={{ color: t.textMuted }}>
          {initialValues ? 'Editing contact details' : 'Will be added to system contacts & linked here'}
        </p>
        <div className="flex gap-2">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors min-h-[40px]">
            Cancel
          </button>
          <button onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors inline-flex items-center gap-1.5 disabled:opacity-60 min-h-[40px]"
            style={{ background: t.accent }}>
            {isSubmitting && <Loader2 className="h-3 w-3 animate-spin" />}
            {submitLabel ?? (initialValues ? CONTACT_FORM_LABELS.saveChanges : CONTACT_FORM_LABELS.createAndLink)}
          </button>
        </div>
      </div>
    </div>
  );
}
